import { Api } from '@axiom/ui';
import type {
  Account,
  AccountExpansion,
  AfcSubmissionsItem,
  CandidateAccount,
  Contact,
} from '@axiom/validation';
import { AccountConst, CandidateAccountConst } from '@axiom/const';
import { stringify } from 'qs';

import { EnvUtil } from '../utils/env-util';
import ApiHelper from '../lib/api-helper';

const api = new ApiHelper('ACCOUNTS');

export const getAccount = async (accountId: Account['id']) => {
  const { data } = await api.GET(`/accounts/${accountId}`, {
    expand: [AccountConst.Expansions.teamUserInfo],
  });

  return data;
};

class AccountLegacyApiClass extends Api {
  constructor() {
    super({
      domain: EnvUtil.clientApiBase,
    });
  }

  readAccount(accountId: Account['id'], body: AccountExpansion = {}) {
    return super.read<{ data: Account }>({
      endpoint: `/accounts/${accountId}`,
      encodeQuery: false,
      query: body,
      method: 'GET',
    });
  }

  async createShareAccount(
    accountId: Account['id'],
    body: {
      contacts: Array<Pick<Contact, 'firstName' | 'lastName' | 'email'>>;
      message: string | null;
      email?: Contact['email'];
      submissionId: AfcSubmissionsItem['id'];
    }
  ) {
    return super.write<{ data: Array<{ email: string; statusCode: number }> }>({
      endpoint: `/accounts/${accountId}/share`,
      method: 'POST',
      body,
    });
  }

  async refreshAccount(accountId: Account['id'], body: AccountExpansion = {}) {
    return super.invalidate(`/accounts/${accountId}/?${stringify(body)}`);
  }

  readAccountCandidates(accountId: Account['id']) {
    return super.read<{ data: CandidateAccount[] }>({
      endpoint: `/accounts/${accountId}/candidates`,
      encodeQuery: false,
      query: {
        expand: [CandidateAccountConst.Expansions.PracticeAreaInfo],
      },
      method: 'GET',
    });
  }

  async refreshAccountCandidates(accountId: Account['id']) {
    return super.invalidate(`/accounts/${accountId}/candidates`);
  }

  updateAccountsCandidates = async (
    accountId: Account['id'],
    candidateId: CandidateAccount['id'],
    body: Partial<CandidateAccount>
  ) => {
    return super.write<{ data: CandidateAccount[] }>({
      endpoint: `/accounts/${accountId}/candidates/${candidateId}`,
      method: 'PATCH',
      body,
    });
  };
}

export const AccountLegacyApi = new AccountLegacyApiClass();
